import type { JobsClient } from "@databricks/sdk-jobs/v2";
import type { PipelinesClient } from "@databricks/sdk-pipelines/v2";
import type { VectorSearchClient } from "@databricks/sdk-vectorsearch/v1";
import type { ModelServingClient } from "@databricks/sdk-modelserving/v1";
import type { SchemasClient } from "@databricks/sdk-uc-schemas/v1";
import type { SecretsClient } from "@databricks/sdk-secrets/v1";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import type { DatabricksPrincipal } from "./identity.js";
import { databricksCredentials, databricksIdentity } from "./identity.js";
import { createDatabricksNativeClients } from "./native-sdk.js";

export const DATABRICKS_CERTIFICATION_RESOURCE_PREFIX = "fabric-harness-authoring-cert-";
const DATABRICKS_CERTIFICATION_SQL_RESOURCE_PREFIX = "fabric_harness_authoring_cert_";

export interface DatabricksCertificationSweepClients {
  jobs: Pick<JobsClient, "listJobsIter" | "deleteJob">;
  pipelines: Pick<PipelinesClient, "listIter" | "delete">;
  vectorSearch: Pick<
    VectorSearchClient,
    "listEndpointsIter" | "listVectorIndexIter" | "deleteVectorIndex" | "deleteEndpoint"
  >;
  modelServing: Pick<ModelServingClient, "listInferenceEndpoints" | "deleteInferenceEndpoint">;
  schemas: Pick<SchemasClient, "listSchemasIter" | "deleteSchema">;
  secrets: Pick<SecretsClient, "listScopes" | "deleteScope">;
  /** Required only when workspaceRoots are swept; Workspace object APIs are absent from sdk-js 0.21. */
  rawProtocol?: DatabricksRawProtocolClient;
}

export interface DatabricksCertificationSweepOptions {
  catalogs?: string[];
  workspaceRoots?: string[];
  dryRun?: boolean;
}

export interface DatabricksCertificationWorkspaceSweepOptions
  extends DatabricksCertificationSweepOptions {
  host: string;
  principal: DatabricksPrincipal;
  fetchImpl?: typeof fetch;
}

export interface DatabricksCertificationSweepResult {
  dryRun: boolean;
  discovered: Array<{ kind: string; id: string }>;
  deleted: Array<{ kind: string; id: string }>;
}

/**
 * Build the official generated clients plus the private Workspace-object adapter and sweep one
 * workspace. This is the supported operational entrypoint; callers never construct raw transport.
 */
export function sweepDatabricksAuthoringCertificationWorkspace(
  options: DatabricksCertificationWorkspaceSweepOptions,
): Promise<DatabricksCertificationSweepResult> {
  const tokenProvider = databricksIdentity(options.principal);
  const clients = createDatabricksNativeClients({
    host: options.host,
    credentials: databricksCredentials(options.principal),
    tokenProvider,
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
  });
  return sweepDatabricksAuthoringCertification(clients, {
    ...(options.catalogs ? { catalogs: options.catalogs } : {}),
    ...(options.workspaceRoots ? { workspaceRoots: options.workspaceRoots } : {}),
    ...(options.dryRun !== undefined ? { dryRun: options.dryRun } : {}),
  });
}

/** Remove only resources carrying the reserved protected-certification prefix. */
export async function sweepDatabricksAuthoringCertification(
  clients: DatabricksCertificationSweepClients,
  options: DatabricksCertificationSweepOptions = {},
): Promise<DatabricksCertificationSweepResult> {
  const dryRun = options.dryRun ?? true;
  const discovered: Array<{ kind: string; id: string }> = [];
  const deletions: Array<{ kind: string; id: string; remove: () => Promise<unknown> }> = [];
  const add = (kind: string, id: string, remove: () => Promise<unknown>) => {
    if (!isCertificationResource(id)) return;
    discovered.push({ kind, id });
    deletions.push({ kind, id, remove });
  };

  for await (const job of clients.jobs.listJobsIter({ expandTasks: false })) {
    const name = job.settings?.name;
    const id = job.jobId;
    if (name && id !== undefined && job.settings?.tags?.["fabric-harness:managed"] === "true") {
      add("job", `${name}:${id}`, () => clients.jobs.deleteJob({ jobId: id }));
    }
  }

  for await (const pipeline of clients.pipelines.listIter({})) {
    if (pipeline.pipelineId && pipeline.name) {
      add("pipeline", `${pipeline.name}:${pipeline.pipelineId}`, () =>
        clients.pipelines.delete({ pipelineId: pipeline.pipelineId }),
      );
    }
  }

  for await (const endpoint of clients.vectorSearch.listEndpointsIter({})) {
    const name = endpoint.name;
    if (!name || !isCertificationResource(name)) continue;
    for await (const index of clients.vectorSearch.listVectorIndexIter({ endpointName: name })) {
      if (index.name) {
        add("ai-search-index", index.name, () =>
          clients.vectorSearch.deleteVectorIndex({ name: index.name }),
        );
      }
    }
    add("ai-search-endpoint", name, () => clients.vectorSearch.deleteEndpoint({ name }));
  }

  const serving = await clients.modelServing.listInferenceEndpoints({});
  for (const endpoint of serving.endpoints ?? []) {
    if (endpoint.name) {
      add("serving-endpoint", endpoint.name, () =>
        clients.modelServing.deleteInferenceEndpoint({ name: endpoint.name }),
      );
    }
  }

  for (const catalog of options.catalogs ?? []) {
    for await (const schema of clients.schemas.listSchemasIter({ catalogName: catalog })) {
      const fullName = schema.fullName ?? (schema.name ? `${catalog}.${schema.name}` : undefined);
      if (fullName && schema.properties?.["fabric-harness.managed"] === "true") {
        add("schema", fullName, () =>
          clients.schemas.deleteSchema({ fullNameArg: fullName, force: false }),
        );
      }
    }
  }

  const scopes = await clients.secrets.listScopes({});
  for (const scope of scopes.scopes ?? []) {
    if (scope.name) {
      add("secret-scope", scope.name, () => clients.secrets.deleteScope({ scope: scope.name }));
    }
  }

  for (const root of options.workspaceRoots ?? []) {
    const rawProtocol = clients.rawProtocol;
    if (!rawProtocol) {
      throw new Error(
        "Workspace certification sweeping requires the explicit raw protocol client.",
      );
    }
    let listing: { objects?: Array<{ path?: string }> };
    try {
      listing = await rawProtocol.get<{ objects?: Array<{ path?: string }> }>(
        "/api/2.0/workspace/list",
        { path: root },
      );
    } catch (error) {
      if (!/404|RESOURCE_DOES_NOT_EXIST|NOT_FOUND/i.test(String(error))) throw error;
      listing = {};
    }
    for (const object of listing.objects ?? []) {
      if (object.path) {
        add("workspace-object", object.path, () =>
          rawProtocol.post(
            "/api/2.0/workspace/delete",
            { path: object.path, recursive: true },
            {},
            { retry: "never" },
          ),
        );
      }
    }
  }

  const deleted: Array<{ kind: string; id: string }> = [];
  if (!dryRun) {
    const failures: Array<{ kind: string; id: string; error: string }> = [];
    for (const deletion of deletions.reverse()) {
      try {
        await deletion.remove();
        deleted.push({ kind: deletion.kind, id: deletion.id });
      } catch (error) {
        failures.push({
          kind: deletion.kind,
          id: deletion.id,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
    if (failures.length) {
      throw new Error(
        `Databricks certification sweeper leaked resources: ${JSON.stringify(failures)}`,
      );
    }
  }
  return { dryRun, discovered, deleted };
}

function isCertificationResource(id: string): boolean {
  return (
    id.includes(DATABRICKS_CERTIFICATION_RESOURCE_PREFIX) ||
    id.includes(DATABRICKS_CERTIFICATION_SQL_RESOURCE_PREFIX)
  );
}
